/**
 * Helper for /public/landing-v2/* asset paths.
 *
 * Returns absolute URL paths that the browser fetches at runtime —
 * NOT webpack-bundled imports. This means:
 *   - no Webpack content-hash → simpler diffs
 *   - no re-build when an asset changes (just refresh)
 *   - bundle stays small (a 19-image collection adds ~0 KB to JS)
 *   - lazy-load via <picture> + native `loading="lazy"` is trivial
 *
 * The flat `lpImg.*` shape (instead of a generic `lpAsset()` only) is
 * deliberate: it gives IDE autocomplete inside LandingV2.jsx so the
 * call-site reads `lpImg.hero.mobile` instead of a stringly-typed
 * path that would silently break on rename.
 *
 * PNG-Fallback paths exist for every WebP so a future `<picture>`
 * element can degrade on Safari < 14 (≈ 1% of EU traffic in 2026 —
 * still worth not breaking).
 */

const BASE = '/landing-v2';

export const lpAsset = (subpath) =>
  `${BASE}${subpath.startsWith('/') ? '' : '/'}${subpath}`;

export const lpImg = {
  hero: {
    mobile:     lpAsset('/images/hero/start-mobile.webp'),
    mobile_png: lpAsset('/images/hero/start-mobile.png'),
  },
  dealflow: {
    mobile:         lpAsset('/images/dealflow/dealcard-mobile.webp'),
    mobile_png:     lpAsset('/images/dealflow/dealcard-mobile.png'),
    mobile_alt:     lpAsset('/images/dealflow/dealcard-alt-mobile.webp'),
    mobile_alt_png: lpAsset('/images/dealflow/dealcard-alt-mobile.png'),
    desktop:        lpAsset('/images/dealflow/dealflow-desktop.webp'),
    desktop_png:    lpAsset('/images/dealflow/dealflow-desktop.png'),
  },
  aiAdvisor: {
    mobile:      lpAsset('/images/ai-advisor/ai-mobile.webp'),
    mobile_png:  lpAsset('/images/ai-advisor/ai-mobile.png'),
    desktop:     lpAsset('/images/ai-advisor/ai-desktop.webp'),
    desktop_png: lpAsset('/images/ai-advisor/ai-desktop.png'),
  },
  dealcardDetail: {
    mobile:                 lpAsset('/images/dealcard-detail/detail-mobile.webp'),
    mobile_png:             lpAsset('/images/dealcard-detail/detail-mobile.png'),
    mobile_description:     lpAsset('/images/dealcard-detail/detail-description-mobile.webp'),
    mobile_description_png: lpAsset('/images/dealcard-detail/detail-description-mobile.png'),
    desktop:                lpAsset('/images/dealcard-detail/detail-desktop.webp'),
    desktop_png:            lpAsset('/images/dealcard-detail/detail-desktop.png'),
  },
  topDeals: {
    mobile:      lpAsset('/images/top-deals/topdeals-mobile.webp'),
    mobile_png:  lpAsset('/images/top-deals/topdeals-mobile.png'),
    desktop:     lpAsset('/images/top-deals/topdeals-desktop.webp'),
    desktop_png: lpAsset('/images/top-deals/topdeals-desktop.png'),
  },
  favorites: {
    mobile:      lpAsset('/images/favorites/favorites-mobile.webp'),
    mobile_png:  lpAsset('/images/favorites/favorites-mobile.png'),
    desktop:     lpAsset('/images/favorites/favorites-desktop.webp'),
    desktop_png: lpAsset('/images/favorites/favorites-desktop.png'),
  },
  shareAndEarn: {
    mobile_main:           lpAsset('/images/share-and-earn/share-mobile.webp'),
    mobile_main_png:       lpAsset('/images/share-and-earn/share-mobile.png'),
    mobile_conditions:     lpAsset('/images/share-and-earn/conditions-mobile.webp'),
    mobile_conditions_png: lpAsset('/images/share-and-earn/conditions-mobile.png'),
    desktop:               lpAsset('/images/share-and-earn/share-desktop.webp'),
    desktop_png:           lpAsset('/images/share-and-earn/share-desktop.png'),
    desktop_alt:           lpAsset('/images/share-and-earn/share-alt-desktop.webp'),
    desktop_alt_png:       lpAsset('/images/share-and-earn/share-alt-desktop.png'),
  },
  compare: {
    mobile:     lpAsset('/images/compare/comparison-mobile.webp'),
    mobile_png: lpAsset('/images/compare/comparison-mobile.png'),
  },
  preferences: {
    desktop:     lpAsset('/images/preferences/preferences-desktop.webp'),
    desktop_png: lpAsset('/images/preferences/preferences-desktop.png'),
    // Mobile Preferences screenshot shipped in the final ZIP
    // (`mobile fotos/preferences mobil.png`) — added 2026-06-14.
    mobile:      lpAsset('/images/preferences/preferences-mobile.webp'),
    mobile_png:  lpAsset('/images/preferences/preferences-mobile.png'),
  },
};

export const lpVideo = {
  // Phase C.2 (2026-06-14) — compressed deliveries via ffmpeg libx264.
  // Mobile 40 MB → 3.1 MB (-92%), Desktop 103 MB → 5.8 MB (-94%).
  // The original files are kept on disk for re-compression (different
  // CRFs / scales), but .gitignore strips the 103 MB .mov so only the
  // *-720p / *-1280 deliveries are committed and deploy-safe.
  //
  // WebM/VP9 was intentionally skipped on the ARM64 pod (libvpx-vp9 is
  // single-threaded and unusably slow on aarch64 — speed=0.022x). All
  // modern browsers (incl. Safari 14+) accept H.264 MP4 natively, so
  // WebM is a size-optimization-only win we defer until either the
  // build moves to x86_64 or we accept a longer compression window.
  mobile_dealflow: {
    mp4:    lpAsset('/videos/mobile/dealflow-swipe-topdeals-720p.mp4'),
    webm:   null,
    poster: lpAsset('/posters/mobile-dealflow-poster.jpg'),
  },
  desktop_dashboard: {
    mp4:    lpAsset('/videos/desktop/dashboard-topdeals-favorites-1280.mp4'),
    webm:   null,
    poster: lpAsset('/posters/desktop-dashboard-poster.jpg'),
  },
};
